import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Search, UploadCloud, Trash2, Copy, FolderPlus, Loader2, Image as ImageIcon, Link2 } from 'lucide-react';
import { API_BASE } from '../config/api';

const API = API_BASE;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

function flattenCategoryTree(categories) {
    const list = [...(categories || [])];
    const byParent = new Map();
    list.forEach((c) => {
        const p = c.parent_id == null || c.parent_id === '' ? '__root__' : String(c.parent_id);
        if (!byParent.has(p)) byParent.set(p, []);
        byParent.get(p).push(c);
    });
    byParent.forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.name || '').localeCompare(b.name || '')));
    const out = [];
    const walk = (parentKey, depth) => {
        const kids = byParent.get(parentKey) || [];
        kids.forEach((node) => {
            out.push({ ...node, depth });
            walk(String(node.id), depth + 1);
        });
    };
    walk('__root__', 0);
    return out;
}

export default function MediaGalleryModal({ open, onClose, authToken, uploadsLocked = false, canCurateAiLibrary = false }) {
    const { t } = useTranslation('builder');
    const [libraryMode, setLibraryMode] = useState('tenant'); /* tenant | ai_global */
    const isAiMode = canCurateAiLibrary && libraryMode === 'ai_global';
    const [categories, setCategories] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [qDebounced, setQDebounced] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [detail, setDetail] = useState(null);
    const [usageById, setUsageById] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [newCatParent, setNewCatParent] = useState('');
    const [categoryMsg, setCategoryMsg] = useState('');
    const [categorySuccess, setCategorySuccess] = useState('');
    const [uploadFeedback, setUploadFeedback] = useState(null);
    const [detailUrlCopied, setDetailUrlCopied] = useState(false);
    const urlCopiedTimerRef = useRef(null);
    const [editName, setEditName] = useState('');
    const [editTags, setEditTags] = useState('');
    const [editCats, setEditCats] = useState([]);
    const [editRole, setEditRole] = useState('content');
    const [editMood, setEditMood] = useState('');
    const [editStatus, setEditStatus] = useState('ready');
    const [uploadTags, setUploadTags] = useState('');
    const [uploadRole, setUploadRole] = useState('content');

    const limit = 48;
    /* Em modo IA o plano de upload do tenant não bloqueia a curadoria */
    const mediaActionsLocked = isAiMode ? false : uploadsLocked;

    const flatCats = useMemo(() => flattenCategoryTree(categories), [categories]);

    const loadCategories = useCallback(() => {
        if (!authToken) return;
        fetch(`${API}/media/categories`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then(async (r) => {
                const d = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setCategorySuccess('');
                    setCategoryMsg(d.error || t('mediaCategoriesLoadError', { defaultValue: 'Não foi possível carregar as categorias.' }));
                    setCategories([]);
                    return;
                }
                setCategoryMsg('');
                setCategories(d.categories || []);
            })
            .catch(() => {
                setCategorySuccess('');
                setCategoryMsg(t('mediaCategoriesLoadError', { defaultValue: 'Não foi possível carregar as categorias.' }));
                setCategories([]);
            });
    }, [authToken, t]);

    const fetchMediaPage = useCallback((appendOff) => {
        if (!authToken) return Promise.resolve();
        setLoading(true);
        if (isAiMode) {
            const params = new URLSearchParams({ limit: String(limit), offset: String(appendOff) });
            if (qDebounced) params.set('q', qDebounced);
            if (roleFilter) params.set('image_role', roleFilter);
            return fetch(`${API}/ai-media?${params}`, { headers: { Authorization: `Bearer ${authToken}` } })
                .then((r) => r.json())
                .then((d) => {
                    const chunk = d.assets || [];
                    setTotal(Number(d.total) || 0);
                    if (appendOff === 0) setUploads(chunk);
                    else setUploads((prev) => [...prev, ...chunk]);
                })
                .finally(() => setLoading(false));
        }
        const params = new URLSearchParams({ limit: String(limit), offset: String(appendOff) });
        if (qDebounced) params.set('q', qDebounced);
        if (categoryFilter) params.set('category_id', categoryFilter);
        return fetch(`${API}/media?${params}`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json())
            .then((d) => {
                const chunk = d.uploads || [];
                setTotal(Number(d.total) || 0);
                if (appendOff === 0) setUploads(chunk);
                else setUploads((prev) => [...prev, ...chunk]);
            })
            .finally(() => setLoading(false));
    }, [authToken, qDebounced, categoryFilter, roleFilter, limit, isAiMode]);

    useEffect(() => {
        const tmr = setTimeout(() => setQDebounced(q.trim()), 320);
        return () => clearTimeout(tmr);
    }, [q]);

    useEffect(() => {
        if (!open) return;
        if (uploadsLocked && canCurateAiLibrary) setLibraryMode('ai_global');
    }, [open, uploadsLocked, canCurateAiLibrary]);

    useEffect(() => {
        if (!open || !authToken) return;
        setCategoryMsg('');
        setCategorySuccess('');
        setUploadFeedback(null);
        if (!isAiMode) loadCategories();
    }, [open, authToken, loadCategories, isAiMode]);

    useEffect(() => () => {
        if (urlCopiedTimerRef.current) clearTimeout(urlCopiedTimerRef.current);
    }, []);

    useEffect(() => {
        if (!open || !authToken) return;
        setSelected(new Set());
        setDetail(null);
        fetchMediaPage(0);
    }, [open, authToken, qDebounced, categoryFilter, roleFilter, fetchMediaPage, libraryMode]);

    const fetchUsage = (id) => {
        if (!authToken || isAiMode || usageById[id]) return;
        fetch(`${API}/media/${id}/usage`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json())
            .then((d) => setUsageById((prev) => ({ ...prev, [id]: d })))
            .catch(() => {});
    };

    useEffect(() => {
        if (detail?.id) fetchUsage(detail.id);
    }, [detail?.id]);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    };

    const openDetail = (u) => {
        setDetail(u);
        setDetailUrlCopied(false);
        setEditName(u.display_name || u.filename || '');
        setEditTags((u.tags || []).join(', '));
        setEditCats([...(u.category_ids || [])]);
        setEditRole(u.image_role || 'content');
        setEditMood(u.mood || '');
        setEditStatus(u.status || 'ready');
    };

    const saveDetail = () => {
        if (!authToken || !detail) return;
        setSaving(true);
        if (isAiMode) {
            fetch(`${API}/ai-media/${detail.id}`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    display_name: editName,
                    tags: editTags,
                    image_role: editRole,
                    mood: editMood,
                    status: editStatus,
                }),
            })
                .then((r) => r.json())
                .then((d) => {
                    if (d.asset) {
                        setUploads((prev) => prev.map((x) => (x.id === d.asset.id ? d.asset : x)));
                        setDetail(d.asset);
                    }
                })
                .finally(() => setSaving(false));
            return;
        }
        fetch(`${API}/media/${detail.id}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
                display_name: editName,
                tags: editTags,
                category_ids: editCats,
            }),
        })
            .then((r) => r.json())
            .then((d) => {
                if (d.upload) {
                    setUploads((prev) => prev.map((x) => (x.id === d.upload.id ? d.upload : x)));
                    setDetail(d.upload);
                }
            })
            .finally(() => setSaving(false));
    };

    const copyUrl = (url) => {
        navigator.clipboard?.writeText(url)?.then(() => {
            setDetailUrlCopied(true);
            if (urlCopiedTimerRef.current) clearTimeout(urlCopiedTimerRef.current);
            urlCopiedTimerRef.current = setTimeout(() => setDetailUrlCopied(false), 2000);
        });
    };

    const onCreateCategory = async (e) => {
        e?.preventDefault();
        setCategoryMsg('');
        setCategorySuccess('');
        if (!authToken || !newCatName.trim()) return;
        try {
            const r = await fetch(`${API}/media/categories`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newCatName.trim(),
                    parent_id: newCatParent || null,
                }),
            });
            let d = {};
            try {
                d = await r.json();
            } catch (_) {
                d = {};
            }
            if (!r.ok) {
                setCategorySuccess('');
                setCategoryMsg(d.error || t('mediaCategorySaveError', { defaultValue: 'Não foi possível criar a categoria.' }));
                return;
            }
            const createdName = d.category?.name || newCatName.trim();
            setNewCatName('');
            setCategoryMsg('');
            setCategorySuccess(
                t('mediaCategoryCreated', { name: createdName, defaultValue: `Categoria “${createdName}” criada com sucesso.` })
            );
            loadCategories();
        } catch {
            setCategorySuccess('');
            setCategoryMsg(t('mediaCategorySaveError', { defaultValue: 'Não foi possível criar a categoria.' }));
        }
    };

    const onUploadFiles = async (files) => {
        if (mediaActionsLocked || !files?.length || !authToken) return;
        const valid = Array.from(files).filter((f) => ACCEPTED_TYPES.includes(f.type));
        if (!valid.length) {
            setUploadFeedback({
                level: 'error',
                text: t('uploadErrorTypes', { defaultValue: 'Use apenas: JPG, PNG, WebP, GIF ou SVG.' }),
            });
            return;
        }
        setUploading(true);
        setUploadFeedback(null);
        const fd = new FormData();
        valid.forEach((f) => fd.append('files', f));
        if (isAiMode) {
            fd.append('tags', uploadTags || '');
            fd.append('image_role', uploadRole || 'content');
            fd.append('status', 'ready');
            try {
                const r = await fetch(`${API}/ai-media/batch-upload`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${authToken}` },
                    body: fd,
                });
                let d = {};
                try { d = await r.json(); } catch (_) { d = {}; }
                if (!r.ok) {
                    setUploadFeedback({
                        level: 'error',
                        text: d.error || t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }),
                    });
                    return;
                }
                const ups = d.assets || [];
                const errs = d.errors || [];
                if (ups.length && !errs.length) {
                    setUploadFeedback({
                        level: 'success',
                        text: t('aiMediaUploadOk', {
                            count: ups.length,
                            defaultValue: `${ups.length} arquivo(s) enviado(s) à biblioteca IA.`,
                        }),
                    });
                } else if (ups.length && errs.length) {
                    setUploadFeedback({
                        level: 'warning',
                        text: `${t('mediaUploadPartial', { ok: ups.length, fail: errs.length, defaultValue: `${ups.length} enviado(s), ${errs.length} com falha.` })} ${errs.map((e) => `${e.name}: ${e.error}`).join(' · ')}`,
                    });
                } else {
                    setUploadFeedback({
                        level: 'error',
                        text: (errs.length ? errs.map((e) => `${e.name}: ${e.error}`).join(' · ') : null)
                            || t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }),
                    });
                }
                await fetchMediaPage(0);
            } catch {
                setUploadFeedback({ level: 'error', text: t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }) });
            } finally {
                setUploading(false);
            }
            return;
        }
        fd.append('tags', '');
        const catIds = categoryFilter ? [String(categoryFilter).trim()] : [];
        fd.append('category_ids', JSON.stringify(catIds));
        try {
            const r = await fetch(`${API}/media/batch-upload`, { method: 'POST', headers: { Authorization: `Bearer ${authToken}` }, body: fd });
            let d = {};
            try {
                d = await r.json();
            } catch (_) {
                d = {};
            }
            if (!r.ok) {
                setUploadFeedback({
                    level: 'error',
                    text: d.error || t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }),
                });
                return;
            }
            const ups = d.uploads || [];
            const errs = d.errors || [];
            if (ups.length && !errs.length) {
                setUploadFeedback({
                    level: 'success',
                    text:
                        catIds.length > 0
                            ? t('mediaUploadOkCategorized', {
                                  count: ups.length,
                                  defaultValue: `${ups.length} arquivo(s) enviado(s) e vinculado(s) à categoria selecionada.`,
                              })
                            : t('mediaUploadOk', { count: ups.length, defaultValue: `${ups.length} arquivo(s) enviado(s).` }),
                });
            } else if (ups.length && errs.length) {
                setUploadFeedback({
                    level: 'warning',
                    text:
                        `${t('mediaUploadPartial', { ok: ups.length, fail: errs.length, defaultValue: `${ups.length} enviado(s), ${errs.length} com falha.` })} ${errs.map((e) => `${e.name}: ${e.error}`).join(' · ')}`,
                });
            } else if (errs.length) {
                setUploadFeedback({
                    level: 'error',
                    text: errs.map((e) => `${e.name}: ${e.error}`).join(' · '),
                });
            } else {
                setUploadFeedback({
                    level: 'error',
                    text: t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }),
                });
            }
            await fetchMediaPage(0);
            loadCategories();
        } catch {
            setUploadFeedback({ level: 'error', text: t('uploadErrorSend', { defaultValue: 'Erro ao enviar.' }) });
        } finally {
            setUploading(false);
        }
    };

    const batchDelete = () => {
        if (!authToken || selected.size === 0) return;
        if (isAiMode) {
            if (!window.confirm(t('aiMediaBatchDeleteConfirm', { defaultValue: 'Excluir as imagens selecionadas da biblioteca IA?' }))) return;
            fetch(`${API}/ai-media/batch-delete`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: [...selected] }),
            })
                .then(() => {
                    setSelected(new Set());
                    setDetail(null);
                    fetchMediaPage(0);
                });
            return;
        }
        const hasInUse = [...selected].some((id) => usageById[id]?.in_use);
        const msg = hasInUse
            ? t('mediaBatchDeleteConfirmInUse', { defaultValue: 'Algumas imagens podem estar em uso em projetos. Excluir mesmo assim?' })
            : t('mediaBatchDeleteConfirm', { defaultValue: 'Excluir as imagens selecionadas?' });
        if (!window.confirm(msg)) return;
        fetch(`${API}/media/batch-delete`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: [...selected] }),
        })
            .then(() => {
                setSelected(new Set());
                setDetail(null);
                fetchMediaPage(0);
            });
    };

    const canLoadMore = uploads.length < total;

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100020] flex items-center justify-center bg-black/55 p-3 md:p-6"
            role="dialog"
            aria-modal="true"
            onPointerDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="flex flex-col w-full max-w-6xl h-[min(92vh,880px)] rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0 gap-3">
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <ImageIcon size={22} className={isAiMode ? 'text-violet-600' : 'text-blue-600'} />
                        {isAiMode
                            ? t('aiMediaGalleryTitle', { defaultValue: 'Biblioteca IA (mídia Global)' })
                            : t('mediaGalleryTitle', { defaultValue: 'Galeria de mídia' })}
                    </h2>
                    <div className="flex items-center gap-2">
                        {canCurateAiLibrary && (
                            <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden text-xs font-semibold">
                                <button
                                    type="button"
                                    onClick={() => setLibraryMode('tenant')}
                                    className={`px-2.5 py-1.5 ${!isAiMode ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                                >
                                    {t('mediaLibraryTenant', { defaultValue: 'Minha galeria' })}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setLibraryMode('ai_global')}
                                    className={`px-2.5 py-1.5 ${isAiMode ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                                >
                                    {t('mediaLibraryAi', { defaultValue: 'Biblioteca IA' })}
                                </button>
                            </div>
                        )}
                        <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                            <X size={22} />
                        </button>
                    </div>
                </div>
                <p className={`px-4 py-2 text-[11px] border-b ${isAiMode ? 'text-violet-800 dark:text-violet-200 bg-violet-50/90 dark:bg-violet-950/25 border-violet-100 dark:border-violet-900/40' : 'text-slate-600 dark:text-slate-400 bg-amber-50/90 dark:bg-amber-950/25 border-amber-100 dark:border-amber-900/40'}`}>
                    {isAiMode
                        ? t('aiMediaGalleryHint', {
                            defaultValue: 'Curadoria Mestra: tags e papéis (hero, pessoas, ilustração…) para o match na montagem do layout. Não mistura com a galeria do cliente.',
                        })
                        : (uploadsLocked
                            ? t('moduleUploadLockedHint')
                            : t('mediaGalleryDragHint', { defaultValue: 'Para arrastar uma imagem até o bloco Imagem no palco, use a gaveta lateral «Uploads» (miniaturas recentes).' }))}
                </p>

                <div className="flex flex-1 min-h-0 flex-col md:flex-row">
                    <div className="flex-1 flex flex-col min-w-0 min-h-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
                        <div className="p-3 flex flex-wrap gap-2 items-center border-b border-slate-100 dark:border-slate-800 shrink-0">
                            <div className="relative flex-1 min-w-[140px] max-w-md">
                                <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder={t('mediaSearchPlaceholder', { defaultValue: 'Buscar por nome ou tags…' })}
                                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200"
                                />
                            </div>
                            {isAiMode ? (
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-2 text-slate-800 dark:text-slate-200 max-w-[200px]"
                                >
                                    <option value="">{t('aiMediaAllRoles', { defaultValue: 'Todos os papéis' })}</option>
                                    {['hero', 'content', 'gallery', 'background', 'icon', 'people', 'illustration', 'other'].map((r) => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            ) : (
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-2 text-slate-800 dark:text-slate-200 max-w-[200px]"
                                >
                                    <option value="">{t('mediaAllCategories', { defaultValue: 'Todas as categorias' })}</option>
                                    {flatCats.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {'—'.repeat(c.depth) + (c.depth ? ' ' : '') + c.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {selected.size > 0 && (
                                <button
                                    type="button"
                                    onClick={batchDelete}
                                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
                                >
                                    <Trash2 size={16} />
                                    {t('mediaBatchDelete', { defaultValue: 'Excluir' })} ({selected.size})
                                </button>
                            )}
                        </div>
                        <div className="p-3 shrink-0 flex flex-wrap gap-2 items-end border-b border-slate-100 dark:border-slate-800">
                            {isAiMode ? (
                                <div className="flex flex-wrap gap-2 items-center flex-1 min-w-[200px]">
                                    <input
                                        value={uploadTags}
                                        onChange={(e) => setUploadTags(e.target.value)}
                                        placeholder={t('aiMediaTagsPlaceholder', { defaultValue: 'Tags: excel, negócios, hero…' })}
                                        className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800 flex-1 min-w-[140px]"
                                    />
                                    <select
                                        value={uploadRole}
                                        onChange={(e) => setUploadRole(e.target.value)}
                                        className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                    >
                                        {['hero', 'content', 'gallery', 'background', 'icon', 'people', 'illustration', 'other'].map((r) => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                                <form onSubmit={onCreateCategory} className="flex flex-wrap gap-2 items-center">
                                    <input
                                        value={newCatName}
                                        onChange={(e) => {
                                            setNewCatName(e.target.value);
                                            if (categoryMsg) setCategoryMsg('');
                                            if (categorySuccess) setCategorySuccess('');
                                        }}
                                        placeholder={t('categoryNamePlaceholder', { defaultValue: 'Nova categoria' })}
                                        className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800 flex-1 min-w-[120px]"
                                    />
                                    <select
                                        value={newCatParent}
                                        onChange={(e) => setNewCatParent(e.target.value)}
                                        className="text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800 max-w-[160px]"
                                    >
                                        <option value="">{t('mediaTopLevelCategory', { defaultValue: 'Raiz' })}</option>
                                        {flatCats.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {'—'.repeat(c.depth) + (c.depth ? ' ' : '') + c.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button type="submit" className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900">
                                        <FolderPlus size={16} />
                                        {t('newCategory', { defaultValue: 'Criar' })}
                                    </button>
                                </form>
                                {categorySuccess ? (
                                    <p className="text-sm text-green-600 dark:text-green-400" role="status">
                                        {categorySuccess}
                                    </p>
                                ) : null}
                                {categoryMsg ? (
                                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                                        {categoryMsg}
                                    </p>
                                ) : null}
                            </div>
                            )}
                            {!mediaActionsLocked ? (
                            <label className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border-2 border-dashed cursor-pointer ${isAiMode ? 'border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40' : 'border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40'}`}>
                                <UploadCloud size={18} />
                                {uploading ? <Loader2 className="animate-spin" size={18} /> : t('mediaUploadDrop', { defaultValue: 'Enviar imagens' })}
                                <input
                                    type="file"
                                    accept={ACCEPTED_TYPES.join(',')}
                                    multiple
                                    className="hidden"
                                    disabled={uploading}
                                    onChange={(e) => { onUploadFiles(e.target.files); e.target.value = ''; }}
                                />
                            </label>
                            ) : null}
                            {uploadFeedback ? (
                                <div className="w-full mt-1">
                                    <p
                                        role={uploadFeedback.level === 'error' ? 'alert' : 'status'}
                                        className={`text-sm rounded-lg px-2 py-1.5 ${
                                            uploadFeedback.level === 'success'
                                                ? 'text-green-800 dark:text-green-200 bg-green-50 dark:bg-green-950/50'
                                                : uploadFeedback.level === 'warning'
                                                  ? 'text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40'
                                                  : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40'
                                        }`}
                                    >
                                        {uploadFeedback.text}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 min-h-0">
                            {loading && uploads.length === 0 ? (
                                <div className="flex justify-center py-12 text-slate-500"><Loader2 className="animate-spin" size={32} /></div>
                            ) : (
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                                    {uploads.map((u) => {
                                        const sel = selected.has(u.id);
                                        const use = usageById[u.id];
                                        return (
                                            <div
                                                key={u.id}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(u); fetchUsage(u.id); } }}
                                                onClick={() => { openDetail(u); fetchUsage(u.id); }}
                                                className={`relative aspect-square rounded-xl overflow-hidden border-2 cursor-pointer text-left transition-all ${sel ? 'border-blue-600 ring-2 ring-blue-400' : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'} ${detail?.id === u.id ? 'ring-2 ring-amber-400' : ''}`}
                                            >
                                                <button
                                                    type="button"
                                                    aria-checked={sel}
                                                    onClick={(e) => { e.stopPropagation(); toggleSelect(u.id); fetchUsage(u.id); }}
                                                    className={`absolute top-1 right-1 z-10 w-6 h-6 min-w-[1.5rem] shrink-0 rounded-md border-2 flex items-center justify-center text-[11px] font-bold shadow-md ${
                                                        sel
                                                            ? 'bg-blue-600 border-blue-800 text-white ring-2 ring-white'
                                                            : 'bg-white border-slate-700 text-slate-800 ring-2 ring-white shadow-[0_1px_4px_rgba(0,0,0,0.35)] hover:border-blue-500'
                                                    }`}
                                                >
                                                    {sel ? '✓' : ''}
                                                </button>
                                                <img src={u.url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
                                                {use?.in_use && (
                                                    <span className="absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                                                        {t('mediaInUse', { defaultValue: 'Em uso' })}
                                                    </span>
                                                )}
                                                {isAiMode && u.image_role && (
                                                    <span className="absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-600 text-white">
                                                        {u.image_role}
                                                    </span>
                                                )}
                                                <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/65 px-1 truncate">
                                                    {u.display_name || u.filename}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {canLoadMore && (
                                <div className="flex justify-center pt-4">
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => fetchMediaPage(uploads.length)}
                                        className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 className="animate-spin inline" size={18} /> : t('mediaLoadMore', { defaultValue: 'Carregar mais' })}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <aside className="w-full md:w-80 shrink-0 flex flex-col border-t md:border-t-0 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        {detail ? (
                            <div className="p-4 flex flex-col gap-3 overflow-y-auto max-h-[40vh] md:max-h-none md:flex-1">
                                <div className="aspect-video rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700">
                                    <img src={detail.url} alt="" className="w-full h-full object-contain" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500">{t('mediaDisplayName', { defaultValue: 'Nome' })}</label>
                                    <input
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="mt-1 w-full text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500">{t('mediaTagsHint', { defaultValue: 'Tags (separadas por vírgula)' })}</label>
                                    <input
                                        value={editTags}
                                        onChange={(e) => setEditTags(e.target.value)}
                                        className="mt-1 w-full text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                    />
                                </div>
                                {isAiMode ? (
                                    <>
                                        <div>
                                            <label className="text-xs font-medium text-slate-500">{t('aiMediaRole', { defaultValue: 'Papel da imagem' })}</label>
                                            <select
                                                value={editRole}
                                                onChange={(e) => setEditRole(e.target.value)}
                                                className="mt-1 w-full text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                            >
                                                {['hero', 'content', 'gallery', 'background', 'icon', 'people', 'illustration', 'other'].map((r) => (
                                                    <option key={r} value={r}>{r}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-slate-500">{t('aiMediaMood', { defaultValue: 'Tom / mood' })}</label>
                                            <input
                                                value={editMood}
                                                onChange={(e) => setEditMood(e.target.value)}
                                                className="mt-1 w-full text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                                placeholder="ex: corporativo, acolhedor"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-slate-500">{t('aiMediaStatus', { defaultValue: 'Status' })}</label>
                                            <select
                                                value={editStatus}
                                                onChange={(e) => setEditStatus(e.target.value)}
                                                className="mt-1 w-full text-sm rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-2 bg-white dark:bg-slate-800"
                                            >
                                                <option value="draft">draft</option>
                                                <option value="ready">ready</option>
                                                <option value="archived">archived</option>
                                            </select>
                                        </div>
                                    </>
                                ) : (
                                <div>
                                    <label className="text-xs font-medium text-slate-500">{t('mediaCategoriesAssign', { defaultValue: 'Categorias' })}</label>
                                    <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg p-2 bg-white dark:bg-slate-800 space-y-1">
                                        {flatCats.map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editCats.includes(c.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setEditCats((prev) => [...prev, c.id]);
                                                        else setEditCats((prev) => prev.filter((x) => x !== c.id));
                                                    }}
                                                />
                                                <span>{'—'.repeat(c.depth) + (c.depth ? ' ' : '') + c.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                )}
                                {!isAiMode && (
                                <div className="text-xs text-slate-600 dark:text-slate-400">
                                    <span className="font-medium">{t('mediaUsageLabel', { defaultValue: 'Uso em projetos' })}: </span>
                                    {usageById[detail.id]?.layouts?.length
                                        ? usageById[detail.id].layouts.map((l) => l.course_name).join(', ')
                                        : t('mediaNotInUse', { defaultValue: 'Nenhum projeto neste tenant' })}
                                </div>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={saveDetail}
                                        disabled={saving}
                                        className={`flex-1 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50 ${isAiMode ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                    >
                                        {saving ? '…' : t('mediaSaveMeta', { defaultValue: 'Salvar' })}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => copyUrl(detail.url)}
                                        className={`inline-flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                                            detailUrlCopied
                                                ? 'border-green-500 bg-green-50 text-green-800 dark:bg-green-950/50 dark:text-green-200 dark:border-green-600'
                                                : 'border-slate-300 dark:border-slate-600'
                                        }`}
                                        title={t('copyUrl')}
                                    >
                                        <Copy size={16} />{' '}
                                        {detailUrlCopied ? t('urlCopied', { defaultValue: 'Copiado!' }) : 'URL'}
                                    </button>
                                </div>
                                <p className="text-[10px] text-slate-400 flex items-start gap-1">
                                    <Link2 size={12} className="mt-0.5 shrink-0" />
                                    {detail.url}
                                </p>
                            </div>
                        ) : (
                            <div className="p-6 text-sm text-slate-500 flex flex-col items-center justify-center h-full text-center">
                                {t('mediaSelectItemHint', { defaultValue: 'Clique em uma imagem para ver detalhes, editar nome/tags e categorias.' })}
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>,
        document.body
    );
}
